import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { Buyer } from '@ps/core-models';
import { Nullable } from '@ps/utils';
import {
  AdvancedFeasibilityFilters,
  Survey,
  SurveyLocationUploadResult,
  SurveyMetadataFormState,
  SurveyQualificationsFormState,
  SurveyQuotasFormState,
} from '@ps/survey-utils';

export const SurveyCreationActions = createActionGroup({
  source: 'Survey Creation',
  events: {
    Init: props<{ surveyId: Nullable<number> }>(),
    'Create Standard Survey': emptyProps(),
    'Update Metadata': props<{ metadata: SurveyMetadataFormState }>(),
    'Update Qualifications': props<{
      qualifications: SurveyQualificationsFormState;
    }>(),
    'Update Quotas': props<{ quotas: SurveyQuotasFormState }>(),
    'Save Survey': emptyProps(),
    'Save And Clone Survey': emptyProps(),
    'Save And Create New': emptyProps(),
    'Save And Launch': emptyProps(),
    'Save And Edit Audience': emptyProps(),
    'Save And Exit': emptyProps(),
    'Discard Survey Changes': emptyProps(),
    'Save Survey And Disconnect Impersonation': emptyProps(),
    'Save Survey And Switch Impersonated Buyer': props<{ buyer: Buyer }>(),
    'Toggle Manual Processing': props<{
      companyId: number;
      enabled: boolean;
    }>(),
    /**
     * Commits the Feasibility drawer's pending Advanced Filters.
     *
     * `clearUploadedPsidsFor` is legacy parity: when the committed
     * type switched from Include to Exclude, the surveyId is passed
     * to fire the PSID clear call for that survey.
     */
    'Apply Advanced Filters': props<{
      filters: AdvancedFeasibilityFilters;
      clearUploadedPsidsFor: Nullable<number>;
    }>(),
    'Upload Location Codes': props<{
      countryCode: string;
      qualificationCode: number;
      qualificationName: string;
      file: File;
    }>(),
    'Clear Location Codes': props<{
      qualificationCode: number;
      qualificationName: string;
    }>(),
    'Download Location Codes Template': props<{
      countryCode: string;
      qualificationCode: number;
    }>(),
    'Download Validated Location File': props<{
      qualificationCode: number;
    }>(),
    'Create Survey From Template': props<{
      templateId: number;
      deleteSurveyId: Nullable<number>;
    }>(),
    Reset: emptyProps(),
  },
});

export const SurveyCreationApiActions = createActionGroup({
  source: 'Survey Creation API',
  events: {
    'Create Standard Survey Success': props<{ survey: Survey }>(),
    'Create Standard Survey Failure': props<{ error: string }>(),
    'Save Survey Success': props<{ survey: Survey }>(),
    'Save Survey Failure': props<{ error: string }>(),
    'Clone Survey Success': props<{ survey: Survey }>(),
    'Clone Survey Failure': props<{ error: string }>(),
    'Load Survey For Edit Success': props<{ survey: Survey }>(),
    'Load Survey For Edit Failure': props<{ error: string }>(),
    'Upload Location Codes Success': props<{
      qualificationCode: number;
      qualificationName: string;
      result: SurveyLocationUploadResult;
    }>(),
    'Upload Location Codes Failure': props<{
      qualificationCode: number;
      error: string;
    }>(),
    'Create Survey From Template Success': props<{
      survey: Survey;
      replacedSurveyId: Nullable<number>;
    }>(),
    'Create Survey From Template Failure': props<{ error: string }>(),
    'Load Survey Tags Success': props<{ tagIds: number[] }>(),
    'Load Survey Tags Failure': props<{ error: string }>(),
    'Link Survey Tags Success': emptyProps(),
    'Link Survey Tags Failure': props<{ error: string }>(),
    'Save Survey And Exit Success': props<{ survey: Survey }>(),
    'Save Survey And Disconnect Impersonation Success': props<{
      survey: Survey;
    }>(),
    'Save Survey And Switch Impersonated Buyer Success': props<{
      survey: Survey;
      buyer: Buyer;
    }>(),
  },
});
